import { Link } from '@inertiajs/react'
import { Play } from 'lucide-react'
import Navbar from '@/components/layout/Navbar'
import EpisodeCard from '@/components/ui/EpisodeCard'
import { Button } from '@/components/ui/Button'
import type { EpisodeCard as EpisodeCardType } from '@/types'

interface Props {
  latest_episodes: EpisodeCardType[]
}

const FEATURES = [
  {
    label: '⌘K',
    title: 'Instant search',
    desc: 'Search titles, descriptions, tags, and markdown Show Notes with highlighted results.',
  },
  {
    label: '85%',
    title: 'Progress tracking',
    desc: 'Resume each screencast, mark lessons complete, and show series completion percentage.',
  },
  {
    label: 'Pro',
    title: 'Free + subscription',
    desc: 'Free episodes for discovery, $9/month Pro for the full catalog and new releases.',
  },
]

const STATS = [
  { value: '20 Episodes MVP', label: '' },
  { value: '500 Signups', label: '' },
  { value: '30 Pro Subs', label: '' },
  { value: '$270 MRR', label: '' },
]

export default function Index({ latest_episodes }: Props) {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="py-20 px-4 text-center max-w-3xl mx-auto">
        <h1 className="text-5xl font-bold text-gray-900 leading-tight mb-6">
          Practical screencasts for developers who want code that runs
        </h1>
        <p className="text-lg text-gray-500 mb-10">
          5-20 minute episodes, focused topics, copyable Show Notes, progress tracking, and
          a Pro library built for modern Rails + React developers.
        </p>
        <div className="flex items-center justify-center gap-4">
          <Link href="/episodes">
            <Button size="lg">Watch free episodes</Button>
          </Link>
          <Link href="/pricing">
            <Button variant="secondary" size="lg">View pricing</Button>
          </Link>
        </div>
      </section>

      {/* Hero preview card */}
      <section className="max-w-4xl mx-auto px-4 mb-20">
        <div className="bg-gray-100 rounded-2xl p-6 flex gap-6 items-start">
          <div className="flex-1 bg-gray-800 rounded-xl aspect-video flex items-center justify-center">
            <div className="w-16 h-16 bg-brand rounded-full flex items-center justify-center">
              <Play className="w-7 h-7 text-white fill-white ml-1" />
            </div>
          </div>
          <div className="w-64 shrink-0 font-mono text-sm text-gray-700 pt-2">
            <p className="text-xs tracking-widest text-gray-400 mb-3">SHOW NOTES</p>
            <p className="font-semibold mb-2">Build a Stripe Pro gate in Rails 8</p>
            <ol className="space-y-1 text-gray-500 list-decimal list-inside text-xs">
              <li>Add pay gem + Stripe webhook</li>
              <li>Protect Pro episodes</li>
              <li>Resume playback after checkout</li>
            </ol>
          </div>
        </div>
      </section>

      {/* Features section */}
      <section className="bg-gray-50 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <p className="text-xs font-semibold tracking-widest text-brand uppercase mb-3">MVP EXPERIENCE</p>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Everything a developer needs to keep learning
          </h2>
          <p className="text-gray-500">
            Browse episodes, search by tag or Show Notes, unlock Pro content, and return to the exact timestamp where you stopped.
          </p>
        </div>

        <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-6 mb-16">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-white rounded-xl p-6 border border-gray-100">
              <p className="text-brand font-mono text-sm font-semibold mb-2">{f.label}</p>
              <h3 className="font-bold text-gray-900 mb-2">{f.title}</h3>
              <p className="text-sm text-gray-500">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* Stats bar */}
        <div className="max-w-4xl mx-auto bg-white rounded-xl border border-gray-100 px-6 py-6 grid grid-cols-4 gap-4 text-center">
          {STATS.map((s) => (
            <div key={s.value}>
              <p className="font-bold text-gray-900 text-lg">{s.value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Latest episodes */}
      {latest_episodes.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 py-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Latest episodes</h2>
            <Link href="/episodes" className="text-sm text-brand hover:underline">View all →</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {latest_episodes.map((ep, i) => (
              <EpisodeCard key={ep.id} episode={ep} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-gray-100 py-10 text-center text-sm text-gray-400">
        <p>© {new Date().getFullYear()} GetCasts. Built for developers.</p>
      </footer>
    </div>
  )
}
